/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import React, { FC, useEffect, useState } from "react";
import { DateTime } from "luxon";
import {
  LaddaButton,
  DateTimeFormat,
  PERMISSIONS,
  MMddyyyy,
  ddMMyyyy,
} from "@advapacs/common";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { IRootState } from "@src/config/reducers";
import { updateDateTimeFormat } from "./ducks/operators";
import { useAppDispatch } from "@src/config/store";
import { getTypeDomain, hasPermission } from "@src/utils/utils";

const DateTimeSetting: FC = () => {
  const { dateFormatLoading } = useSelector(
    (state: IRootState) => state.settingGeneralReducer
  );
  const { account, permissions } = useSelector(
    (state: IRootState) => state.loginReducer
  );

  const dispatch = useAppDispatch();
  const { t } = useTranslation("admin");
  const [dateTimeFormatForm, setDateTimeFormat] = useState<DateTimeFormat>({
    dateFormat: account?.localisation?.dateFormat,
    timeFormat: account?.localisation?.timeFormat,
    dateTimeFormat: account?.localisation?.dateTimeFormat,
  });
  const [datePreview, setDatePreview] = useState("");
  const [timePreview, setTimePreview] = useState("");
  const [hasChange, setHasChange] = useState(false);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setDateTimeFormat({ ...dateTimeFormatForm, [name]: value });
  };

  useEffect(() => {
    setDateTimeFormat({
      dateFormat: account?.localisation?.dateFormat,
      timeFormat: account?.localisation?.timeFormat,
      dateTimeFormat: account?.localisation?.dateTimeFormat,
    });
  }, [account]);

  const saveValueChange = () => {
    const payload: DateTimeFormat = {
      ...dateTimeFormatForm,
      dateTimeFormat: `${dateTimeFormatForm?.dateFormat} ${dateTimeFormatForm?.timeFormat}`,
    };
    dispatch(updateDateTimeFormat(payload));
  };

  useEffect(() => {
    if (dateTimeFormatForm?.dateFormat) {
      setDatePreview(
        DateTime.fromJSDate(new Date()).toFormat(dateTimeFormatForm.dateFormat)
      );
    }
    if (dateTimeFormatForm?.timeFormat) {
      setTimePreview(
        DateTime.fromJSDate(new Date()).toFormat(dateTimeFormatForm.timeFormat)
      );
    }
    setHasChange(
      account?.localisation?.dateFormat !== dateTimeFormatForm?.dateFormat ||
        account?.localisation?.timeFormat !== dateTimeFormatForm?.timeFormat
    );
  }, [dateTimeFormatForm, account]);

  return (
    <div>
      <h3 className="card-title">{t("setting_general.date_time_format")}</h3>
      <div className="account-setting no-padding-mobile flexbox direction-cl-mobile">
        <div className="label-setting">
          <span className="label-setting p-b-xs">
            {t("setting_general.date_format")}
          </span>
          <div className="m-t-xs">
            <div className="date-format cursor-pointer">
              <label htmlFor="df1">
                <input
                  id="df1"
                  name="dateFormat"
                  type="radio"
                  value={ddMMyyyy}
                  checked={dateTimeFormatForm?.dateFormat === ddMMyyyy}
                  onChange={handleInputChange}
                />
                <span className="ml-1">{ddMMyyyy}</span>
              </label>
            </div>
            <div className="date-format">
              <label htmlFor="df2">
                <input
                  id="df2"
                  name="dateFormat"
                  type="radio"
                  value={MMddyyyy}
                  checked={dateTimeFormatForm?.dateFormat === MMddyyyy}
                  onChange={handleInputChange}
                />
                <span className="ml-1">{MMddyyyy}</span>
              </label>
            </div>
            <div className="preview">
              <strong>
                {t("setting_general.preview")}: {datePreview}
              </strong>
            </div>
          </div>
        </div>

        <div className="p-lr-xs select-setting no-padding-mobile">
          <span className="label-setting">
            {t("setting_general.time_format")}
          </span>
          <div className="m-t-xs">
            <div className="time-format cursor-pointer">
              <label htmlFor="tf1">
                <input
                  id="tf1"
                  name="timeFormat"
                  type="radio"
                  value="HH:mm"
                  checked={dateTimeFormatForm.timeFormat === "HH:mm"}
                  onChange={handleInputChange}
                />
                <span className="ml-1">HH:mm</span>
              </label>
            </div>
            <div className="time-format">
              <label htmlFor="tf2">
                <input
                  id="tf2"
                  name="timeFormat"
                  type="radio"
                  value="hh:mm a"
                  checked={dateTimeFormatForm.timeFormat === "hh:mm a"}
                  onChange={handleInputChange}
                />
                <span className="ml-1">hh:mm AM/PM</span>
              </label>
            </div>
            <div className="preview">
              <strong>
                {t("setting_general.preview")}: {timePreview}
              </strong>
            </div>
          </div>
        </div>
      </div>
      {(getTypeDomain() === "individual" ||
        hasPermission(permissions, [PERMISSIONS.MANAGE_GENERAL_SETTING])) && (
        <LaddaButton
          btnName={t("save")}
          onClickHandler={() => saveValueChange()}
          loading={dateFormatLoading}
          disabled={!hasChange}
          className="btn btn-primary btn-fixed setting-btn-height mt-2"
        />
      )}
    </div>
  );
};

export default DateTimeSetting;
